import { useCallback, useEffect, useRef, useState } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import { isAutomatedBrowser, isBrowserEnvironment } from './isBrowserEnvironment';
import { getAnalyticsCookieRules } from './analyticsCookies';
import { createConsentHandlers } from './consentHandlers';

function reloadPage() {
    window.location.reload();
}

function createEngineConfig(handlers) {
    return {
        autoShow: false,
        categories: {
            necessary: { enabled: true, readOnly: true },
            analytics: {
                autoClear: {
                    cookies: getAnalyticsCookieRules(),
                },
            },
        },
        language: {
            default: 'en',
            translations: { en: {} },
        },
        ...handlers,
    };
}

function readConsent() {
    return {
        isReady: true,
        hasChoice: CookieConsent.validConsent(),
        analyticsAccepted: CookieConsent.acceptedCategory('analytics'),
    };
}

// Keeps the visitor's choice and the Google Analytics rules. The library runs without any
// interface of its own, so the cookie bar, the dialog and the gear are React components.
function useCookieConsent({ reload = reloadPage, measurementId } = {}) {
    const [consent, setConsent] = useState({ isReady: false, hasChoice: false, analyticsAccepted: false });
    const optionsRef = useRef({ reload, measurementId });

    useEffect(() => {
        optionsRef.current = { reload, measurementId };
    }, [reload, measurementId]);

    useEffect(() => {
        if (!isBrowserEnvironment() || isAutomatedBrowser()) {
            return undefined;
        }

        let isActive = true;
        const handlers = createConsentHandlers(() => optionsRef.current);
        const engine = CookieConsent.run(createEngineConfig(handlers));
        Promise.resolve(engine).then(() => {
            if (isActive) {
                setConsent(readConsent());
            }
        });

        return () => {
            isActive = false;
        };
    }, []);

    const choose = useCallback((categories) => {
        CookieConsent.acceptCategory(categories);
        setConsent(readConsent());
    }, []);

    return {
        ...consent,
        acceptAll: useCallback(() => choose('all'), [choose]),
        rejectAll: useCallback(() => choose([]), [choose]),
        saveAnalytics: useCallback((isEnabled) => choose(isEnabled ? ['analytics'] : []), [choose]),
    };
}

export default useCookieConsent;
